import * as React from "react";
import { FormattedMessage } from "react-intl";
import { Col, Form, Input, Row, Switch, Divider, InputNumber } from "antd";
import {
  Widget,
  WidgetType,
  NavigationButton,
  NavigationConfiguration
} from "@models/widget";
import {
  createRequest,
  updateRequest,
} from "@actions/widget";
import Navigation from "@components/custom/Navigation";
import ItemsManager from "@components/widgets/ItemsManager";
import LocationSettings from "@components/widgets/LocationSettings";
import widgetApi from "@api/widget";
import withFormWrapper from "@hocs/withFormWrapper";


interface PropsFromState {
  loading: boolean;
  errors?: string;
  form: any;
  widget?: Widget;
  className?: string;
  type: string;
}

interface PropsFromDispatch {
  createRequest: typeof createRequest;
  updateRequest: typeof updateRequest;
}

type WidgetFormAllProps = PropsFromState & PropsFromDispatch;

type WidgetFormStateShape = {
  display_title: string;
  is_active: boolean;
  configuration: any;
  navigation_configuration: NavigationConfiguration | null;
  navigation_button: NavigationButton | null;
  location_reference: any;
  location: string;
  owner: string;
  id?: string;
};

/**
 * This form handle all widget types form
 * All widgest has items
 * All widgets has navigation configuration
 * Navigation configuration in carousels and wideSlider is belongs to the root object ant it is not required but in other types is belong to the items
 * Carousel widgets has a flag option to choose items (automated)
 */
class WidgetForm extends React.Component<
  WidgetFormAllProps,
  WidgetFormStateShape
> {
  navigationRef = null;
  locationRef = null;

  itemsManagerRef = React.createRef();

  constructor(props) {
    super(props);
    const { widget } = props;
    this.state = {
      ...this.getStateFromProps(widget)
    };
  }

  getStateFromProps = (widget: Widget) => {
    //TODO check just once and set default values instead of inline if for each property!
    const configuration = widget ? widget.configuration : {};
    return {
      display_title: widget ? widget.display_title : "",
      is_active: widget ? widget.is_active : true,
      items: configuration && configuration.items ? configuration.items : [],
      automated: configuration && configuration.automated ? configuration.automated : false,
      id: widget ? widget.id : null,
      is_fetched: widget ? widget.is_fetched : false,
      navigation_configuration: configuration && configuration.navigation_configuration ? configuration.navigation_configuration : null,
      navigation_button: configuration && configuration.navigation_button ? configuration.navigation_button : null,
      location: widget ? widget.location : null,
      owner: widget ? widget.owner : null,
      location_reference: widget ? widget.location_reference : null,
      row: widget && widget.row,
      iran_access: widget?.iran_access ?? false
    };
  };

  componentDidMount() {
    if (this.state.id) {
      this.fetchItem();
    }
  }

  fetchItem = () => {
    widgetApi
      .getItem(
        {
          params: {
            id: this.state.id
          }
        },
        this.props.type.replace("_", "-")
      )
      .then(({ data }) => {
        const obj: any = this.getStateFromProps(data);
        this.setState(obj);
      })
      .catch(error => console.log(error));
  };

  onStatusChange(is_active: boolean) {
    this.setState({
      is_active
    });
  }

  /**
   * This function check if the widget has configuration return its values
   * return an object or null
   * Null means that there is a validation issue in the Navigation Component
   */
  getNavigationConfig = () => {
    if (this.navigationRef) {
      const navigationConfig = this.navigationRef.getConfig();
      if (navigationConfig) {
        return navigationConfig;
      } else {
        return null;
      }
    } else {
      return {};
    }
  };

  /**
   * This function return items
   * return an array or null
   * Null means that there is a validation issue in the Items Manager
   */
  getItems = () => {
    const items = this.itemsManagerRef.current && this.itemsManagerRef.current.getItems();
    if (items) {
      return items;
    } else {
      return null;
    }
  };

  getValues = () => {
    let returnValues = null;
    const navigationConfig = this.getNavigationConfig();
    const locationSettings = this.locationRef.getLocationSettings();
    const itemsResult = this.getItems();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err && !!navigationConfig && (!this.hasItemsManager() || (this.hasItemsManager() && !!itemsResult)) && !!locationSettings) {
        returnValues = {
          ...values,
          is_active: this.state.is_active,
          configuration: {
            ...itemsResult,
            ...navigationConfig //In case if carousel which navigation configuration is set in "configuration" Key
          },
          ...locationSettings,
          type: this.props.type
        };
      }
    });

    return {
      payload: returnValues,
      id: this.props.widget ? this.props.widget.id : null
    };
  };

  hasNavigationConfig = () => {
    const { type } = this.props;
    return (
      type === WidgetType.PROMOTED_CAROUSEL ||
      type === WidgetType.REQULAR_CAROUSEL ||
      type === WidgetType.ARTIST_CAROUSEL ||
      type === WidgetType.TAG_CAROUSEL ||
      type === WidgetType.CATEGORY_CAROUSEL ||
      type === WidgetType.FRANCHISE ||
      type === WidgetType.PROMOTED
    );
  };
  
  hasItemsManager = () => {
    return  this.props.type === WidgetType.PROMOTED_POSTER ? false : true
  };
  
  render() {
    const { getFieldDecorator } = this.props.form;
    return (
      <div className={'widget-form-wrapper'}>
        <h1 className={'center-text'}>
          <FormattedMessage
            id={`app.${this.props.type}`}
          />
        </h1>
        
        {this.hasItemsManager() && (
          <ItemsManager
            ref={this.itemsManagerRef}
            automated={this.state.automated}
            items={this.state.items}
            type={this.props.type}
          />
        )}
        

        <Form>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label={
                  <FormattedMessage id="app.title" defaultMessage={`Title`} />
                }
              >
                {getFieldDecorator("display_title", {
                  initialValue: this.state.display_title,
                  rules: [
                    {
                      whitespace: true,
                      required: this.props.type === WidgetType.PROMOTED_POSTER ? false : true,
                      message: (
                        <FormattedMessage
                          id="app.title_is_required"
                          defaultMessage={`Title is required!`}
                        />
                      )
                    }
                  ]
                })(<Input />)}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <FormattedMessage
                    id="app.display_order"
                    defaultMessage={`Display Order`}
                  />
                }
              >
                {getFieldDecorator("row", {
                  initialValue: this.state.row,
                  rules: [
                    {
                      required: true,
                      message: (
                        <FormattedMessage
                          id="app.display_order_is_required"
                          defaultMessage={`Display order is required`}
                        />
                      )
                    }
                  ]
                })(<InputNumber />)}
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={<FormattedMessage id="app.status" defaultMessage={`Status`} />}>
                <Switch
                  defaultChecked={this.state.is_active}
                  onChange={this.onStatusChange.bind(this)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item className={'align-left'} label={<FormattedMessage id="iran_access" defaultMessage={`Iran Access`}/>}>
                {getFieldDecorator("iran_access", {
                  initialValue: this.state.iran_access
                })(
                  <Switch defaultChecked={this.state.iran_access}/>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {this.hasNavigationConfig() && (
          <div>
            <Divider dashed orientation="left">
              <FormattedMessage
                id="app.navigation_settings"
                defaultMessage={`Navigation Settings`}
              />
            </Divider>
            <Navigation
              wrappedComponentRef={ref => (this.navigationRef = ref)}
              widgetType={this.props.type}
              navigation_configuration={this.state.navigation_configuration}
              navigation_button={this.state.navigation_button}
              hasButtonConfig={true}
              required={false}
            />
          </div>
        )}

        <div className={`widget-location-setting-wrapper`}>
          <Divider dashed orientation="left">
            <FormattedMessage
              id="app.widget_location"
              defaultMessage={`Location Settings`}
            />
          </Divider>
          <LocationSettings
            wrappedComponentRef={ref => (this.locationRef = ref)}
            owner={this.state.owner}
            location_reference={this.state.location_reference}
            location={this.state.location}
          />
        </div>
      </div>
    );
  }
}


export default withFormWrapper(Form.create({ name: "widget-form" })(WidgetForm));
